/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';

declare module '@emotion/react' {
  export interface Theme {
    palette: {
      // Navigation & Layout
      navLinkColor: string;
      layoutBorderBottomColor: string;

      // Background Colors
      tertiaryBgColor: string;
      bgWhiteColor: string;
      pageBgColor: string;
      brandBackdrop: string;
      lightGrayBg: string;
      veryLightGrayBg: string;
      blueGrayBg: string;

      // Primary & Accent Colors
      HetheraPrimary: string;
      HetheraPrimaryLight: string;
      blueColor: string;
      greenColor: string;

      // Form Highlight Colors
      logiFormBorderHighlight: string;
      logiFormBackgroundHighlight: string;

      // Red/Error Colors
      redFontColor: string;
      redHoverFontColor: string;
      errorRed: string;

      // Border Colors
      darkBorderColor: string;
      darkestBorderColor: string;
      borderColor: string;
      lightBorder: string;
      mediumBorder: string;
      grayBorder: string;

      // Text Colors
      blackColor: string;
      HetheraBlack: string;
      darkTextColor: string;
      primaryTextColor: string;
      secondaryTextColor: string;
      tertiaryTextColor: string;
      grayTextColor: string;
      lightGrayText: string;
      mediumGrayText: string;
      formFooterText: string;
      formDescriptionText: string;

      // Highlight & Neutral Colors
      highlightColor: string;
      neutralGray: string;
      lightNeutralGray: string;

      // Warning Colors
      warningBgColor: string;
      warningTextColor: string;

      // Overlay & Shadow Colors
      modalBackdrop: string;
      lightOverlay: string;
      boxShadowColor: string;
      lightBoxShadowColor: string;
    };
  }
}

// Colours follow the Hethera website (hethera-eng-website/src/styles/global.css):
// ink #0E1A13, lime #C6F160, cream ground #F4F2EC, white surfaces, warm hairlines.
// The legacy names are kept so every component picks the new look up; "blue"
// names now hold the brand green used for links and accents.
export const theme: Theme = {
  palette: {
    // Navigation & Layout
    navLinkColor: '#0E1A13',
    layoutBorderBottomColor: '#E2DED3',

    // Background Colors
    tertiaryBgColor: '#FAF9F5',
    bgWhiteColor: '#FFFFFF',
    pageBgColor: '#F4F2EC',
    brandBackdrop: '#EEF7D6',
    lightGrayBg: '#F4F2EC',
    veryLightGrayBg: '#EFECE4',
    blueGrayBg: '#FAF9F5',

    // Primary & Accent Colors
    HetheraPrimary: '#1F7A45', // focus borders, accents (readable on white)
    HetheraPrimaryLight: 'rgba(198, 241, 96, 0.22)', // lime tint
    blueColor: '#1F7A45',
    greenColor: '#1F7A45',

    // Form Highlight Colors
    logiFormBorderHighlight: 'rgba(31, 122, 69, 0.55)',
    logiFormBackgroundHighlight: 'rgba(198, 241, 96, 0.08)',

    // Red/Error Colors
    redFontColor: '#B42318',
    redHoverFontColor: '#912018',
    errorRed: '#D92D20',

    // Border Colors
    darkBorderColor: '#D8D3C6',
    darkestBorderColor: '#A9A395',
    borderColor: '#E2DED3',
    lightBorder: '#EFECE4',
    mediumBorder: '#E2DED3',
    grayBorder: '#D8D3C6',

    // Text Colors
    blackColor: '#0E1A13',
    HetheraBlack: '#0E1A13',
    darkTextColor: '#0E1A13',
    primaryTextColor: '#26332B',
    secondaryTextColor: '#3F4A42',
    tertiaryTextColor: '#3F4A42',
    grayTextColor: '#5D665F',
    lightGrayText: '#7A8279',

    mediumGrayText: '#5D665F',
    formFooterText: '#7A8279',
    formDescriptionText: '#4F5A52',

    // Highlight & Neutral Colors
    highlightColor: '#F4F2EC',
    neutralGray: '#26332B',
    lightNeutralGray: '#EFECE4',

    // Warning Colors
    warningBgColor: 'rgba(217, 45, 32, 0.08)',
    warningTextColor: '#D92D20',

    // Overlay & Shadow Colors
    modalBackdrop: 'rgba(14, 26, 19, 0.5)',
    lightOverlay: 'rgba(14, 26, 19, 0.08)',
    boxShadowColor: 'rgba(14, 26, 19, 0.06)',
    lightBoxShadowColor: 'rgba(14, 26, 19, 0.16)',
  },
};

const GlobalStyles = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  * {
    margin: 0;
    font-size: 1em;
  }
  html,
  body {
    height: 100%;
  }
  :root {
    /* Same tokens as the website */
    --ground: #f4f2ec;
    --surface: #ffffff;
    --sunk: #e9e6dc;
    --ink: #0e1a13;
    --ink-2: #26332b;
    --text: #3f4a42;
    --muted: #4f5a52;
    --line: #e2ded3;
    --line-2: #d8d3c6;
    --accent: #c6f160;
    --ok: #1f7a45;
    --dark-text: #eef1ea;
    --dark-muted: #b4beb5;
    --dark-line: #26352c;
    /* Dashboard shell: quiet surfaces, hairlines, soft depth (Vercel/Mercury). */
    --panel: #fbfaf7;
    --hover: rgba(14, 26, 19, 0.045);
    --radius: 14px;
    --radius-sm: 10px;
    --shadow-xs: 0 1px 2px rgba(14, 26, 19, 0.05);
    --shadow-sm: 0 1px 2px rgba(14, 26, 19, 0.04), 0 4px 12px -6px rgba(14, 26, 19, 0.08);
    --shadow-lg: 0 24px 64px -16px rgba(14, 26, 19, 0.28), 0 0 0 1px rgba(14, 26, 19, 0.06);
    --red: #d92d20;
    --amber: #b45309;
    --font-display: 'Bricolage Grotesque', ui-sans-serif, system-ui, sans-serif;
    --font-body: 'Geist', ui-sans-serif, system-ui, sans-serif;
    --font-mono: 'Geist Mono', ui-monospace, monospace;
  }
  body {
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    font-family: var(--font-body);
    font-weight: 400;
    color: var(--ink);
    background: var(--ground);
  }
  h1,
  h2,
  h3 {
    font-family: var(--font-display);
    letter-spacing: -0.02em;
  }
  ::selection {
    background: rgba(198, 241, 96, 0.6);
    color: var(--ink);
  }
  :focus-visible {
    outline: 2px solid var(--ok);
    outline-offset: 2px;
  }
  /* Fields: green border (from the components) plus a soft lime ring. */
  input:focus,
  textarea:focus,
  select:focus {
    box-shadow: 0 0 0 3px rgba(198, 241, 96, 0.45);
  }
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible {
    outline: none;
  }
  /* Search and filter fields above tables: one size, one style. */
  .table-toolbar select,
  .table-toolbar input:not([type='checkbox']):not([type='radio']) {
    height: 40px;
    border: 1px solid #d8d3c6;
    border-radius: 12px;
    background-color: #ffffff;
    color: #0e1a13;
    font-size: 14px;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;
  }
  .table-toolbar select,
  .table-toolbar input[type='date'],
  .table-toolbar input[type='text']:not([placeholder*='Search']) {
    padding: 0 12px;
  }
  .table-toolbar select:hover,
  .table-toolbar input:hover {
    border-color: #a9a395;
  }
  .table-toolbar select:focus,
  .table-toolbar input:focus {
    border-color: #1f7a45;
  }
  /* Native dropdowns: our own chevron, inset from the edge, on every select. */
  select:not([multiple]):not([size]) {
    -webkit-appearance: none;
    appearance: none;
    padding-right: 36px !important;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M3 4.5 6 7.5 9 4.5' stroke='%235D665F' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") !important;
    background-repeat: no-repeat !important;
    background-position: right 12px center !important;
    background-size: 12px 12px !important;
    cursor: pointer;
  }
  select:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  img,
  picture,
  video,
  canvas,
  svg {
    display: block;
    max-width: 100%;
  }
  input,
  button,
  textarea,
  select {
    font: inherit;
  }
  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    overflow-wrap: break-word;
  }
  #root,
  #__next {
    isolation: isolate;
  }
`;

export default GlobalStyles;
